import {ReactElement, useEffect, useState} from "react";
import {
    GetRenovationCostCategoriesWithBudgetYearQuery, useDeleteRenovationCostCategoriesMutation,
    useGetRenovationCostCategoriesWithBudgetYearLazyQuery
} from "../../../__generated__/generated_types";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {Dots} from "@zendeskgarden/react-loaders";
import {Button} from "@zendeskgarden/react-buttons";
import {ReactComponent as EditIcon} from "@zendeskgarden/svg-icons/src/16/pencil-fill.svg";
import {ReactComponent as DeleteIcon} from "@zendeskgarden/svg-icons/src/16/trash-fill.svg";
import {ReactComponent as SearchIcon} from "@zendeskgarden/svg-icons/src/16/search-fill.svg";
import {ReactComponent as AddIcon} from "@zendeskgarden/svg-icons/src/16/plus-circle-stroke.svg";
import {Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import * as css from "./styles/RenovationCategories.module.scss";
import {RenovationCostCategoryForm, RenovationCostCategoryFormProps} from "./components/RenovationCostCategoryForm";
import {toast} from "react-toastify";
import {Modal, Header, Body as ModalBody, Footer, FooterItem} from "@zendeskgarden/react-modals";

export interface RenovationCategoriesProps {
    ready?: () => void;
    budgetYear: number;
}

export function RenovationCategories(props: RenovationCategoriesProps): ReactElement {
    const [getCostCategories, {data: costCategories, loading: costCategoriesLoading}] = useGetRenovationCostCategoriesWithBudgetYearLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [deleteCostCategories,] = useDeleteRenovationCostCategoriesMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [hasFiltered, setHasFiltered] = useState<boolean>(false);
    const [filteredCategories, setFilteredCategories] = useState<GetRenovationCostCategoriesWithBudgetYearQuery["queryRenovationCostCategories"]>([]);
    const [search, setSearch] = useState<string>("");
    const [formVisible, setFormVisible] = useState<boolean>(false);
    const [formInitialData, setFormInitialData] = useState<RenovationCostCategoryFormProps["initialData"]>(undefined);
    const [toDelete, setToDelete] = useState<GetRenovationCostCategoriesWithBudgetYearQuery["queryRenovationCostCategories"][0] | undefined>(undefined);

    function fetchCostCategories(): void {
        getCostCategories({
            variables: {
                budgetYear: props.budgetYear
            }
        });
    }

    function doDeleteCostCategories(id: string): void {
        deleteCostCategories({
            variables: {
                ids: [id]
            }
        }).then(res => {
            if(res.data?.deleteRenovationCostCategories) {
                fetchCostCategories();
                toast.success("Category deleted successfully.");
            } else {
                toast.error("Error deleting category.");
            }
        }).catch(() => {
            toast.error("Error deleting category.");
        }).finally(() => {
            setToDelete(undefined);
        });
    }

    useEffect(() => {
        fetchCostCategories();
    }, []);

    useEffect(() => {
        if(costCategoriesLoading) {
            return;
        }

        setHasFiltered(true);
        if(search === "") {
            const categories = costCategories?.queryRenovationCostCategories ?? [];
            setFilteredCategories(categories.sortBy(c => c.name));
        } else {
            const orig = costCategories?.queryRenovationCostCategories ?? [];
            const filtered = orig.filter(c =>{
                if(c.name.toLowerCase().includes(search.toLowerCase())) {
                    return true;
                }

                if(c.account) {
                    if(c.account.glName.toLowerCase().includes(search.toLowerCase())) {
                        return true;
                    }
                    if(c.account.glNumber && c.account.glNumber.toString().includes(search)) {
                        return true;
                    }
                }
                return false;
            });
            setFilteredCategories(filtered.sortBy(c => c.name));
        }
    }, [costCategories, costCategoriesLoading, search]);

    useEffect(() => {
        if(!hasFiltered && props.ready) {
            props.ready();
        }
    }, [hasFiltered]);

    return <div className={css.mainWrapper}>
        <div className={css.header}>
            <div>
                <form>
                    <Field>
                        <Label hidden>Search</Label>
                        <MediaInput
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            start={<SearchIcon />}
                            placeholder="Search"
                        />
                    </Field>
                </form>
            </div>
            <div>
                <Button isPrimary onClick={() => {
                    setFormInitialData(undefined);
                    setFormVisible(true);
                }}><Button.StartIcon><AddIcon /></Button.StartIcon>Add Category</Button>
            </div>
        </div>
        <Table>
            <colgroup>
                <col style={{width: "20%"}} />
            </colgroup>
            <Head>
                <HeaderRow>
                    <HeaderCell>Actions</HeaderCell>
                    <HeaderCell className={css.centeredTableCell}>Name</HeaderCell>
                    <HeaderCell className={css.centeredTableCell}>Account</HeaderCell>
                </HeaderRow>
            </Head>
            <Body>
                {!hasFiltered ?
                    <Row>
                        <Cell colSpan={3}>Loading<Dots /></Cell>
                    </Row> :
                    (filteredCategories && filteredCategories.length > 0) ?
                            filteredCategories.map(category => {
                                return <Row key={category.id}>
                                    <Cell>
                                        <Button
                                            className={css.actionButton}
                                            isBasic
                                            onClick={() => {
                                                setFormInitialData(category);
                                                setFormVisible(true);
                                            }}
                                        >
                                            <Button.StartIcon><EditIcon /></Button.StartIcon>Edit
                                        </Button>
                                        <Button
                                            className={css.actionButton}
                                            isDanger
                                            isBasic
                                            onClick={() => setToDelete(category)}
                                        >
                                            <Button.StartIcon><DeleteIcon /></Button.StartIcon>Delete
                                        </Button>
                                    </Cell>
                                    <Cell className={css.centeredTableCell}>{category.name}</Cell>
                                    <Cell className={css.centeredTableCell}>{category.account.glNumber} - {category.account.glName}</Cell>
                                </Row>;
                            }):
                            <Row>
                                <Cell colSpan={3}>No categories found</Cell>
                            </Row>
                }
            </Body>
        </Table>
        {formVisible &&
            <RenovationCostCategoryForm
                budgetYear={props.budgetYear}
                initialData={formInitialData}
                onSave={() => {
                    setFormVisible(false);
                    fetchCostCategories();
                }}
            />
        }
        {toDelete &&
            <Modal>
                <Header>Delete {toDelete.name}</Header>
                <ModalBody>Are you sure you want to delete the cost category {toDelete.name}?</ModalBody>
                <Footer>
                    <FooterItem>
                        <Button isBasic onClick={() => setToDelete(undefined)}>Cancel</Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isDanger onClick={() => doDeleteCostCategories(toDelete.id)}>Delete</Button>
                    </FooterItem>
                </Footer>
            </Modal>
        }
    </div>;
}